import { z } from "zod";

export const SOCIAL_PLATFORMS = ["instagram", "tiktok", "x"] as const;
export const SOCIAL_PLATFORM = {
  INSTAGRAM: "instagram",
  TIKTOK: "tiktok",
  X: "x",
} as const satisfies Record<string, SocialPlatform>;
export const SocialPlatformSchema = z.enum(SOCIAL_PLATFORMS);
export type SocialPlatform = z.infer<typeof SocialPlatformSchema>;

export const SOCIAL_PROFILE_STATUSES = ["active", "disabled"] as const;
export const SOCIAL_PROFILE_STATUS = {
  ACTIVE: "active",
  DISABLED: "disabled",
} as const satisfies Record<string, SocialProfileStatus>;
export const SocialProfileStatusSchema = z.enum(SOCIAL_PROFILE_STATUSES);
export type SocialProfileStatus = z.infer<typeof SocialProfileStatusSchema>;

export const SOCIAL_EXTRACTION_STATUSES = [
  "queued",
  "starting",
  "running",
  "importing",
  "stopping",
  "stopped",
  "completed",
  "failed",
  "start_ambiguous",
] as const;
export const SOCIAL_EXTRACTION_STATUS = {
  QUEUED: "queued",
  STARTING: "starting",
  RUNNING: "running",
  IMPORTING: "importing",
  STOPPING: "stopping",
  STOPPED: "stopped",
  COMPLETED: "completed",
  FAILED: "failed",
  START_AMBIGUOUS: "start_ambiguous",
} as const satisfies Record<string, SocialExtractionStatus>;
export const SocialExtractionStatusSchema = z.enum(SOCIAL_EXTRACTION_STATUSES);
export type SocialExtractionStatus = z.infer<
  typeof SocialExtractionStatusSchema
>;

export const SOCIAL_EXTRACTION_TERMINAL_STATUSES = [
  SOCIAL_EXTRACTION_STATUS.STOPPED,
  SOCIAL_EXTRACTION_STATUS.COMPLETED,
  SOCIAL_EXTRACTION_STATUS.FAILED,
  SOCIAL_EXTRACTION_STATUS.START_AMBIGUOUS,
] as const;
export const SocialExtractionTerminalStatusSchema = z.enum(
  SOCIAL_EXTRACTION_TERMINAL_STATUSES,
);
export type SocialExtractionTerminalStatus = z.infer<
  typeof SocialExtractionTerminalStatusSchema
>;

export const SOCIAL_CAPTURE_STATUSES = ["staging", "ready", "failed"] as const;
export const SOCIAL_CAPTURE_STATUS = {
  STAGING: "staging",
  READY: "ready",
  FAILED: "failed",
} as const satisfies Record<string, SocialCaptureStatus>;
export const SocialCaptureStatusSchema = z.enum(SOCIAL_CAPTURE_STATUSES);
export type SocialCaptureStatus = z.infer<typeof SocialCaptureStatusSchema>;

export const SOCIAL_POST_KINDS = [
  "post",
  "reel",
  "video",
  "slideshow",
  "tweet",
] as const;
export const SOCIAL_POST_KIND = {
  POST: "post",
  REEL: "reel",
  VIDEO: "video",
  SLIDESHOW: "slideshow",
  TWEET: "tweet",
} as const satisfies Record<string, SocialPostKind>;
export const SocialPostKindSchema = z.enum(SOCIAL_POST_KINDS);
export type SocialPostKind = z.infer<typeof SocialPostKindSchema>;

export const SOCIAL_MEDIA_KINDS = ["image", "video", "audio"] as const;
export const SOCIAL_MEDIA_KIND = {
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
} as const satisfies Record<string, SocialMediaKind>;
export const SocialMediaKindSchema = z.enum(SOCIAL_MEDIA_KINDS);
export type SocialMediaKind = z.infer<typeof SocialMediaKindSchema>;

export const SOCIAL_ANALYSIS_STATUSES = [
  "queued",
  "running",
  "completed",
  "failed",
] as const;
export const SOCIAL_ANALYSIS_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
} as const satisfies Record<string, SocialAnalysisStatus>;
export const SocialAnalysisStatusSchema = z.enum(SOCIAL_ANALYSIS_STATUSES);
export type SocialAnalysisStatus = z.infer<typeof SocialAnalysisStatusSchema>;

export const SOCIAL_JUDGMENTS = [
  "pass",
  "fail",
  "not_applicable",
  "unable_to_verify",
] as const;
export const SOCIAL_JUDGMENT = {
  PASS: "pass",
  FAIL: "fail",
  NOT_APPLICABLE: "not_applicable",
  UNABLE_TO_VERIFY: "unable_to_verify",
} as const satisfies Record<string, SocialJudgment>;
export const SocialJudgmentSchema = z.enum(SOCIAL_JUDGMENTS);
export type SocialJudgment = z.infer<typeof SocialJudgmentSchema>;

export const SOCIAL_STANDARDS = [
  "message_clarity",
  "brand_voice",
  "visual_accessibility",
  "disclosure",
  "actionability",
] as const;
export const SOCIAL_STANDARD = {
  MESSAGE_CLARITY: "message_clarity",
  BRAND_VOICE: "brand_voice",
  VISUAL_ACCESSIBILITY: "visual_accessibility",
  DISCLOSURE: "disclosure",
  ACTIONABILITY: "actionability",
} as const satisfies Record<string, SocialStandard>;
export const SocialStandardSchema = z.enum(SOCIAL_STANDARDS);
export type SocialStandard = z.infer<typeof SocialStandardSchema>;

export const SOCIAL_EVIDENCE_KINDS = ["text", "media", "metadata"] as const;
export const SOCIAL_EVIDENCE_KIND = {
  TEXT: "text",
  MEDIA: "media",
  METADATA: "metadata",
} as const satisfies Record<string, SocialEvidenceKind>;
export const SocialEvidenceKindSchema = z.enum(SOCIAL_EVIDENCE_KINDS);
export type SocialEvidenceKind = z.infer<typeof SocialEvidenceKindSchema>;

export const SOCIAL_FINDING_SEVERITIES = ["low", "medium", "high"] as const;
export const SOCIAL_FINDING_SEVERITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const satisfies Record<string, SocialFindingSeverity>;
export const SocialFindingSeveritySchema = z.enum(SOCIAL_FINDING_SEVERITIES);
export type SocialFindingSeverity = z.infer<typeof SocialFindingSeveritySchema>;

export const SOCIAL_CLAIM_OUTCOMES = [
  "start",
  "resume",
  "stop",
  "terminal",
  "ambiguous",
] as const;
export const SOCIAL_CLAIM_OUTCOME = {
  START: "start",
  RESUME: "resume",
  STOP: "stop",
  TERMINAL: "terminal",
  AMBIGUOUS: "ambiguous",
} as const satisfies Record<string, SocialClaimOutcome>;
export const SocialClaimOutcomeSchema = z.enum(SOCIAL_CLAIM_OUTCOMES);
export type SocialClaimOutcome = z.infer<typeof SocialClaimOutcomeSchema>;

export const SOCIAL_ANALYSIS_CLAIM_OUTCOMES = ["run", "terminal"] as const;
export const SOCIAL_ANALYSIS_CLAIM_OUTCOME = {
  RUN: "run",
  TERMINAL: "terminal",
} as const satisfies Record<string, SocialAnalysisClaimOutcome>;
export const SocialAnalysisClaimOutcomeSchema = z.enum(
  SOCIAL_ANALYSIS_CLAIM_OUTCOMES,
);
export type SocialAnalysisClaimOutcome = z.infer<
  typeof SocialAnalysisClaimOutcomeSchema
>;
